import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiAdminboostersIdDelete } from '@r/api/generated/client.ts';
import type { BoosterBoosterCollectionReadModel } from '@r/api/generated/client.schemas.ts';
import { useOrvalErrorToast } from '@r/hooks/use-orval-error-toast.ts';
import { BoosterCardsSection } from './booster-cards-section.tsx';
import { EditBoosterDialog } from './edit-booster-dialog.tsx';
import {
  isBoosterActiveByStatus,
  isBoosterFinishedByStatus,
  isBoosterUpcomingByStatus
} from './booster-utils.ts';
import { invalidateBoostersListQueries, useBoostersCollection } from './use-boosters-collection.ts';

/** Trois sections en cartes : en cours, à venir et terminés (comme la maquette). */
export function BoosterList() {
  const { t } = useTranslation();
  const showOrvalErrorToast = useOrvalErrorToast();
  const queryClient = useQueryClient();
  const [editingBoosterId, setEditingBoosterId] = useState<number | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [deletingBoosterId, setDeletingBoosterId] = useState<number | null>(null);

  const boostersQuery = useBoostersCollection();

  const activeBoosters = useMemo(
    () => (boostersQuery.data ?? []).filter(isBoosterActiveByStatus),
    [boostersQuery.data]
  );

  const upcomingBoosters = useMemo(
    () => (boostersQuery.data ?? []).filter(isBoosterUpcomingByStatus),
    [boostersQuery.data]
  );

  const finishedBoosters = useMemo(
    () => (boostersQuery.data ?? []).filter(isBoosterFinishedByStatus),
    [boostersQuery.data]
  );

  const deleteBoosterMutation = useApiAdminboostersIdDelete({
    mutation: {
      onSuccess: () => {
        toast.success(t('boosters.delete.success'));
        setDeletingBoosterId(null);
        invalidateBoostersListQueries(queryClient);
      },
      onError: (error) => {
        setDeletingBoosterId(null);
        showOrvalErrorToast(error, t('boosters.delete.error'));
      }
    }
  });

  const handleRefresh = () => {
    invalidateBoostersListQueries(queryClient);
  };

  const handleEdit = (booster: BoosterBoosterCollectionReadModel) => {
    setEditingBoosterId(booster.id);
    setIsEditDialogOpen(true);
  };

  const handleEditOpenChange = (open: boolean) => {
    setIsEditDialogOpen(open);
    if (!open) {
      setEditingBoosterId(null);
    }
  };

  const handleDelete = (booster: BoosterBoosterCollectionReadModel) => {
    toast(t('boosters.delete.confirm', { title: booster.title }), {
      action: {
        label: t('label.confirm'),
        onClick: () => {
          setDeletingBoosterId(booster.id);
          deleteBoosterMutation.mutate({ id: String(booster.id) });
        }
      },
      cancel: {
        label: t('label.cancel'),
        onClick: () => {}
      }
    });
  };

  return (
    <div className="space-y-10">
      {(boostersQuery.isLoading || activeBoosters.length > 0) && (
        <BoosterCardsSection
          title={t('boosters.list.active.title')}
          boosters={activeBoosters}
          isLoading={boostersQuery.isLoading}
          showEdit
          emptyMessageKey="boosters.list.active.empty"
          onEdit={handleEdit}
        />
      )}

      {(boostersQuery.isLoading || upcomingBoosters.length > 0) && (
        <BoosterCardsSection
          title={t('boosters.list.upcoming.title')}
          boosters={upcomingBoosters}
          isLoading={boostersQuery.isLoading}
          showEdit
          showDelete
          emptyMessageKey="boosters.list.upcoming.empty"
          onEdit={handleEdit}
          onDelete={handleDelete}
          deletingBoosterId={deletingBoosterId}
        />
      )}

      {(boostersQuery.isLoading || finishedBoosters.length > 0) && (
        <BoosterCardsSection
          title={t('boosters.list.finished.title')}
          boosters={finishedBoosters}
          isLoading={boostersQuery.isLoading}
          emptyMessageKey="boosters.list.finished.empty"
        />
      )}

      <EditBoosterDialog
        boosterId={editingBoosterId}
        isOpen={isEditDialogOpen}
        onOpenChange={handleEditOpenChange}
        onSuccess={handleRefresh}
      />
    </div>
  );
}
